import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  const lastUpdated = 'March 10, 2026';

  return (
    <>
      <Helmet>
        <title>Privacy Policy | SERPfinity</title>
        <meta name="description" content="Read the SERPfinity Privacy Policy to learn how we collect, use, store, and protect your personal data when using our SEO and AI search visibility platform." />
            <meta property="og:title" content="Privacy Policy | SERPfinity" />
            <meta property="og:description" content="Read the SERPfinity Privacy Policy to learn how we collect, use, store, and protect your personal data when using our SEO and AI search visibility platform." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://app.serpfinity.ai/privacy" />
            <meta property="og:image" content="https://app.serpfinity.ai/og-image.png" />
            <meta property="og:site_name" content="SERPfinity" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Privacy Policy | SERPfinity" />
            <meta name="twitter:description" content="Read the SERPfinity Privacy Policy to learn how we collect, use, store, and protect your personal data." />
            <meta name="twitter:image" content="https://app.serpfinity.ai/og-image.png" />

      </Helmet>
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-lg text-blue-200">
            Your privacy matters to us. This policy explains how SERPfinity collects, uses, and protects your data.
          </p>
          <p className="text-sm text-blue-300 mt-4">Last Updated: {lastUpdated}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose prose-lg max-w-none">

          {/* Introduction */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Introduction</h2>
            <p className="text-slate-600 leading-relaxed">
              SERPfinity ("we," "our," or "us") operates the SERPfinity AI-Powered Search Visibility Platform 
              accessible at thedigitalhall.club and serpfinity.ai (collectively, the "Service"). This Privacy Policy 
              describes how we collect, use, store, share, and protect your personal information when you visit our 
              websites, create an account, or use our SEO and AI search visibility tools.
            </p>
            <p className="text-slate-600 leading-relaxed mt-4">
              By accessing or using our Service, you acknowledge that you have read and understood this Privacy Policy. 
              If you do not agree with our practices, please do not use the Service.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">2.1 Account & Registration Data</h3>
            <p className="text-slate-600 leading-relaxed">
              When you create an account or subscribe to a plan, we collect:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mt-2 space-y-1">
              <li>Full name (first and last name)</li>
              <li>Email address</li>
              <li>Company name (optional)</li>
              <li>Password (stored in encrypted/hashed form)</li>
              <li>Subscription plan and billing information</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">2.2 SEO & Website Data</h3>
            <p className="text-slate-600 leading-relaxed">
              When you use our platform tools, we process and store data related to your SEO activities, including:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mt-2 space-y-1">
              <li>Website URLs and domain names you submit for analysis</li>
              <li>Keywords you track and research</li>
              <li>Search engine ranking data and SERP analysis results</li>
              <li>Site audit findings, backlink data, and content optimization reports</li>
              <li>AI search visibility scores and competitive analysis results</li>
              <li>Project and campaign configurations</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">2.3 Usage & Analytics Data</h3>
            <p className="text-slate-600 leading-relaxed">
              We automatically collect certain information when you interact with our Service:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mt-2 space-y-1">
              <li>IP address and approximate geographic location</li>
              <li>Browser type, version, and operating system</li>
              <li>Pages visited, features used, and time spent on the platform</li>
              <li>Referring URLs and navigation paths</li>
              <li>Device identifiers and screen resolution</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">2.4 Cookies & Tracking Technologies</h3>
            <p className="text-slate-600 leading-relaxed">
              We use cookies, local storage, and similar technologies to maintain your session, remember preferences, 
              and analyze platform usage. You can manage cookie preferences through your browser settings. Essential 
              cookies required for platform functionality cannot be disabled while using the Service.
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">2.5 Third-Party Integration Data</h3>
            <p className="text-slate-600 leading-relaxed">
              If you connect third-party services (such as Google Search Console or Google Analytics), we may receive 
              data from those services in accordance with the permissions you grant. We only access the data necessary 
              to provide our SEO analysis features.
            </p>
          </section>

          {/* How We Use Your Data */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. How We Use Your Data</h2>
            <p className="text-slate-600 leading-relaxed">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mt-2 space-y-1">
              <li><strong>Service Delivery:</strong> To provide, operate, and maintain our SEO and AI search visibility tools, including keyword tracking, rank monitoring, site audits, content optimization, and competitive analysis.</li>
              <li><strong>Account Management:</strong> To create and manage your account, process subscriptions, and provide customer support.</li>
              <li><strong>Platform Improvement:</strong> To analyze usage patterns, diagnose technical issues, and improve our algorithms, features, and user experience.</li>
              <li><strong>Communications:</strong> To send transactional emails (account confirmations, subscription updates, ranking alerts), and with your consent, marketing communications about new features or promotions.</li>
              <li><strong>Security:</strong> To detect, prevent, and respond to fraud, abuse, security incidents, and technical issues.</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, legal processes, or governmental requests.</li>
            </ul>
          </section>

          {/* User Data Section */}
          <section className="mb-12 bg-blue-50 rounded-xl p-8 border border-blue-100">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Your Data &mdash; Ownership, Access & Control</h2>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.1 You Own Your Data</h3>
            <p className="text-slate-600 leading-relaxed">
              All SEO data, reports, keyword research, site audits, and analysis results generated through your use 
              of SERPfinity belong to you. We do not claim ownership of your content or data. We act as a data 
              processor on your behalf and use your data solely to provide and improve the Service.
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.2 Right to Access</h3>
            <p className="text-slate-600 leading-relaxed">
              You have the right to request a copy of the personal data we hold about you. You can access most of 
              your data directly through your dashboard and profile settings. For a comprehensive data export, 
              contact us at the email provided below.
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.3 Right to Correction</h3>
            <p className="text-slate-600 leading-relaxed">
              If any personal information we hold about you is inaccurate or incomplete, you have the right to 
              request correction. You can update your profile information directly in your account settings.
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.4 Right to Deletion</h3>
            <p className="text-slate-600 leading-relaxed">
              You may request deletion of your account and associated personal data at any time. Upon receiving a 
              verified deletion request, we will delete your personal data within 30 days, except where retention 
              is required by law or for legitimate business purposes (such as resolving disputes or enforcing agreements). 
              Aggregated, anonymized data that cannot identify you may be retained for analytics purposes.
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.5 Right to Data Portability</h3>
            <p className="text-slate-600 leading-relaxed">
              You have the right to receive your data in a structured, commonly used, machine-readable format. 
              Our platform supports data export in CSV and PDF formats for reports, keyword data, and analysis results.
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.6 Right to Opt Out</h3>
            <p className="text-slate-600 leading-relaxed">
              You may opt out of non-essential communications at any time by clicking the unsubscribe link in 
              marketing emails or updating your notification preferences in your account settings. Transactional 
              emails related to your account and subscription cannot be opted out of while maintaining an active account.
            </p>
          </section>

          {/* Data Sharing */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Data Sharing & Third Parties</h2>
            <p className="text-slate-600 leading-relaxed">
              We do not sell, rent, or trade your personal information to third parties for marketing purposes. 
              We may share your data only in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mt-2 space-y-1">
              <li><strong>Service Providers:</strong> We use trusted third-party vendors to help operate our Service, including cloud hosting (AWS), email delivery (Amazon SES), payment processing, and analytics. These providers are contractually bound to protect your data and use it only for the services they provide to us.</li>
              <li><strong>Legal Requirements:</strong> We may disclose your information if required by law, subpoena, court order, or governmental regulation, or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of all or a portion of our assets, your data may be transferred as part of that transaction. We will notify you via email or prominent notice on our Service before your data is transferred and becomes subject to a different privacy policy.</li>
              <li><strong>With Your Consent:</strong> We may share your information for any other purpose with your explicit consent.</li>
            </ul>
          </section>

          {/* Data Security */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Data Security</h2>
            <p className="text-slate-600 leading-relaxed">
              We implement industry-standard security measures to protect your data, including:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mt-2 space-y-1">
              <li>SSL/TLS encryption for all data transmitted between your browser and our servers</li>
              <li>Bcrypt hashing for passwords (we never store passwords in plain text)</li>
              <li>JWT-based authentication with token expiration policies</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Access controls limiting employee access to personal data on a need-to-know basis</li>
              <li>Secure cloud infrastructure with AWS best practices</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              While we strive to protect your personal information, no method of transmission over the Internet or 
              electronic storage is 100% secure. We cannot guarantee absolute security but are committed to 
              promptly notifying affected users in the event of a data breach.
            </p>
          </section>

          {/* Data Retention */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Data Retention</h2>
            <p className="text-slate-600 leading-relaxed">
              We retain your personal data for as long as your account is active or as needed to provide the Service. 
              If you cancel your subscription, your data will be retained for a grace period of 90 days to allow 
              for reactivation, after which it will be scheduled for deletion. Specific retention periods include:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mt-2 space-y-1">
              <li><strong>Account data:</strong> Retained while account is active, plus 90 days after cancellation</li>
              <li><strong>SEO reports and analytics:</strong> Retained per your subscription plan's historical data limits</li>
              <li><strong>Server logs:</strong> Retained for up to 12 months for security and debugging purposes</li>
              <li><strong>Transactional records:</strong> Retained as required by tax and accounting regulations</li>
            </ul>
          </section>

          {/* Children's Privacy */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Children's Privacy</h2>
            <p className="text-slate-600 leading-relaxed">
              Our Service is not directed to individuals under the age of 16. We do not knowingly collect personal 
              information from children. If we become aware that we have collected personal data from a child without 
              parental consent, we will take steps to delete that information promptly. If you believe a child has 
              provided us with personal data, please contact us immediately.
            </p>
          </section>

          {/* International Data Transfers */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">9. International Data Transfers</h2>
            <p className="text-slate-600 leading-relaxed">
              Our Service is hosted in the United States using Amazon Web Services (AWS) infrastructure. If you are 
              accessing the Service from outside the United States, please be aware that your data may be transferred 
              to, stored, and processed in the United States. By using our Service, you consent to the transfer of 
              your information to the United States, where data protection laws may differ from those in your country 
              of residence.
            </p>
          </section>

          {/* GDPR & CCPA */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Your Rights Under GDPR & CCPA</h2>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">For EU/EEA Residents (GDPR)</h3>
            <p className="text-slate-600 leading-relaxed">
              If you are a resident of the European Economic Area, you have additional rights under the General Data 
              Protection Regulation, including the right to access, rectify, erase, restrict processing, object to 
              processing, and data portability. Our legal basis for processing your data includes contractual necessity 
              (to provide the Service), legitimate interests (to improve and secure the platform), and your consent 
              (for marketing communications).
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">For California Residents (CCPA)</h3>
            <p className="text-slate-600 leading-relaxed">
              If you are a California resident, you have the right to know what personal information we collect, 
              request deletion of your personal information, and opt out of the sale of personal information. 
              As stated above, we do not sell your personal information. To exercise your CCPA rights, please 
              contact us using the information provided below.
            </p>
          </section>

          {/* Changes to Policy */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Changes to This Privacy Policy</h2>
            <p className="text-slate-600 leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices, technologies, 
              legal requirements, or other factors. When we make material changes, we will notify you by email 
              (using the email address associated with your account) or by posting a prominent notice on our Service 
              prior to the changes becoming effective. Your continued use of the Service after the effective date 
              constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-12 bg-slate-50 rounded-xl p-8 border border-slate-200">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Contact Us</h2>
            <p className="text-slate-600 leading-relaxed">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, 
              please contact us:
            </p>
            <div className="mt-4 text-slate-700">
              <p><strong>SERPfinity</strong></p>
              <p>Email: <a href="mailto:privacy@serpfinity.ai" className="text-blue-600 hover:text-blue-800 underline">privacy@serpfinity.ai</a></p>
              <p>Website: <a href="https://serpfinity.ai/contact" className="text-blue-600 hover:text-blue-800 underline">serpfinity.ai/contact</a></p>
            </div>
            <p className="text-slate-600 leading-relaxed mt-4">
              We will respond to all legitimate requests within 30 days. In some cases, we may need to verify your 
              identity before processing your request.
            </p>
          </section>

        </div>

        {/* Back to Home */}
        <div className="text-center mt-12 pb-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
      </>
  );
};

export default PrivacyPolicy;
